import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { PageDataService } from '../../core/page-data.service';

export interface FeedLive {
  liveID: number;
  channelName: string | null;
  title: string | null;
  coverImageUrl: string | null;
  viewerCount: number;
  startedAt: string | null;
  endedAt?: string | null;
  status: string | null;
  isLive: boolean;
  replayUrl?: string | null;
  recordingStatus?: string | null;
  recordingDurationSeconds?: number;
}

export interface FeedListing {
  listingID: number;
  listingId: number;
  title: string;
  slug: string;
  imageUrl: string | null;
  price: number | null;
  currencyCode: string | null;
}

export interface FeedSeller {
  sellerID: number;
  sellerId: number;
  sellerName: string;
  userName: string | null;
  avatarUrl: string | null;
}

export interface FeedItem {
  id: number;
  feedID: number;
  feedId: number;
  title: string | null;
  caption: string | null;
  mediaType: 'image' | 'video';
  imageUrl: string | null;
  videoUrl: string | null;
  sourceProvider: string | null;
  sourcePermalink: string | null;
  durationSeconds: number | null;
  publishedAt: string | null;
  listingID: number;
  listingId: number;
  listingTitle: string;
  listingSlug: string;
  listingImageUrl: string | null;
  listingPrice: number | null;
  currencyCode: string | null;
  sellerID: number;
  sellerId: number;
  sellerName: string;
  sellerHandle: string | null;
  sellerAvatarUrl: string | null;
  isLiked: boolean;
  likeCount: number;
  localLikeCount: number;
  providerLikeCount: number | null;
  likeCountSource: string;
  likeCountAvailable: boolean;
  canLike: boolean;
  commentCount: number;
  isFollowing: boolean;
  listing: FeedListing;
  seller: FeedSeller;
  live?: FeedLive;
}

export interface FeedComment {
  id: number;
  commentID: number;
  commentId: number;
  authorName: string | null;
  authorAvatarUrl: string | null;
  comment: string;
  createdAt: string | null;
}

export interface FeedListResult {
  items: FeedItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

@Injectable({ providedIn: 'root' })
export class FeedsService {
  private readonly api = inject(ApiClientService);
  private readonly pageData = inject(PageDataService);

  async initial(limit = 12): Promise<FeedListResult> {
    const seeded = this.pageData.get<unknown>('feeds');
    if (seeded) {
      return this.extractList(seeded, limit);
    }

    return this.list({ page: 1, limit });
  }

  async list(payload: Record<string, unknown>): Promise<FeedListResult> {
    const response = await firstValueFrom(this.api.post<unknown>('feeds', payload));
    return this.extractList(response, Number(payload['limit'] ?? 12));
  }

  async like(feed: FeedItem, like: boolean): Promise<Pick<FeedItem, 'isLiked' | 'likeCount' | 'localLikeCount' | 'providerLikeCount' | 'likeCountSource' | 'likeCountAvailable' | 'canLike'>> {
    const response = await firstValueFrom(this.api.post<unknown>('feedLike', {
      feedID: feed.feedID,
      like
    }));
    const data = this.extractData(response);
    return {
      isLiked: Boolean(data['isLiked'] ?? like),
      likeCount: Number(data['likeCount'] ?? feed.likeCount),
      localLikeCount: Number(data['localLikeCount'] ?? feed.localLikeCount),
      providerLikeCount: this.numberOrNull(data['providerLikeCount'] ?? feed.providerLikeCount),
      likeCountSource: String(data['likeCountSource'] ?? feed.likeCountSource),
      likeCountAvailable: Boolean(data['likeCountAvailable'] ?? feed.likeCountAvailable),
      canLike: Boolean(data['canLike'] ?? feed.canLike)
    };
  }

  async follow(feed: FeedItem, follow: boolean): Promise<boolean> {
    const response = await firstValueFrom(this.api.post<unknown>('feedFollow', {
      sellerID: feed.sellerID,
      follow
    }));
    const data = this.extractData(response);
    return Boolean(data['isFollowing'] ?? data['following'] ?? follow);
  }

  async comments(feedID: number, page = 1, limit = 20): Promise<{ comments: FeedComment[]; hasMore: boolean; total: number }> {
    const response = await firstValueFrom(this.api.post<unknown>('feedComments', {
      feedID,
      page,
      limit
    }));
    const data = this.extractData(response);
    const comments = this.toArray(data['comments']).map((item) => this.normalizeComment(item));

    return {
      comments,
      hasMore: Boolean(data['hasMore']),
      total: Number(data['total'] ?? comments.length)
    };
  }

  async comment(feedID: number, comment: string): Promise<FeedComment | null> {
    const response = await firstValueFrom(this.api.post<unknown>('feedComment', {
      feedID,
      comment
    }));
    const data = this.extractData(response);
    return this.isRecord(data['comment']) ? this.normalizeComment(data['comment']) : null;
  }

  async view(feedID: number, watchDurationMs = 0): Promise<void> {
    try {
      await firstValueFrom(this.api.post<unknown>('feedView', {
        feedID,
        watchDurationMs
      }));
    } catch {
      // View tracking should never interrupt playback.
    }
  }

  private extractList(response: unknown, fallbackLimit: number): FeedListResult {
    const data = this.extractData(response);
    const items = this.toArray(data['items']).map((item) => this.normalizeFeed(item));

    return {
      items,
      page: Number(data['page'] ?? 1),
      limit: Number(data['limit'] ?? fallbackLimit),
      total: Number(data['total'] ?? items.length),
      hasMore: Boolean(data['hasMore'])
    };
  }

  private normalizeFeed(item: Record<string, unknown>): FeedItem {
    const feedID = Number(item['feedID'] ?? item['feedId'] ?? item['id'] ?? 0);
    const listing = this.isRecord(item['listing']) ? item['listing'] : {};
    const seller = this.isRecord(item['seller']) ? item['seller'] : {};
    const live = this.isRecord(item['live']) ? item['live'] : null;

    return {
      id: feedID,
      feedID,
      feedId: feedID,
      title: this.stringOrNull(item['title']),
      caption: this.stringOrNull(item['caption'] ?? item['title']),
      mediaType: item['mediaType'] === 'video' ? 'video' : 'image',
      imageUrl: this.stringOrNull(item['imageUrl']),
      videoUrl: this.stringOrNull(item['videoUrl']),
      sourceProvider: this.stringOrNull(item['sourceProvider']),
      sourcePermalink: this.stringOrNull(item['sourcePermalink']),
      durationSeconds: item['durationSeconds'] === null ? null : Number(item['durationSeconds'] ?? 0),
      publishedAt: this.stringOrNull(item['publishedAt']),
      listingID: Number(item['listingID'] ?? item['listingId'] ?? listing['listingID'] ?? 0),
      listingId: Number(item['listingId'] ?? item['listingID'] ?? listing['listingId'] ?? 0),
      listingTitle: String(item['listingTitle'] ?? listing['title'] ?? ''),
      listingSlug: String(item['listingSlug'] ?? listing['slug'] ?? ''),
      listingImageUrl: this.stringOrNull(item['listingImageUrl'] ?? listing['imageUrl']),
      listingPrice: this.numberOrNull(item['listingPrice'] ?? listing['price']),
      currencyCode: this.stringOrNull(item['currencyCode'] ?? listing['currencyCode']),
      sellerID: Number(item['sellerID'] ?? item['sellerId'] ?? seller['sellerID'] ?? 0),
      sellerId: Number(item['sellerId'] ?? item['sellerID'] ?? seller['sellerId'] ?? 0),
      sellerName: String(item['sellerName'] ?? seller['sellerName'] ?? ''),
      sellerHandle: this.stringOrNull(item['sellerHandle'] ?? seller['userName']),
      sellerAvatarUrl: this.stringOrNull(item['sellerAvatarUrl'] ?? seller['avatarUrl']),
      isLiked: Boolean(item['isLiked']),
      likeCount: Number(item['likeCount'] ?? 0),
      localLikeCount: Number(item['localLikeCount'] ?? item['likeCount'] ?? 0),
      providerLikeCount: this.numberOrNull(item['providerLikeCount']),
      likeCountSource: String(item['likeCountSource'] ?? item['sourceProvider'] ?? 'aymudo'),
      likeCountAvailable: item['likeCountAvailable'] === undefined ? true : Boolean(item['likeCountAvailable']),
      canLike: item['canLike'] === undefined ? !item['sourceProvider'] : Boolean(item['canLike']),
      commentCount: Number(item['commentCount'] ?? 0),
      isFollowing: Boolean(item['isFollowing']),
      listing: {
        listingID: Number(listing['listingID'] ?? item['listingID'] ?? 0),
        listingId: Number(listing['listingId'] ?? item['listingId'] ?? 0),
        title: String(listing['title'] ?? item['listingTitle'] ?? ''),
        slug: String(listing['slug'] ?? item['listingSlug'] ?? ''),
        imageUrl: this.stringOrNull(listing['imageUrl'] ?? item['listingImageUrl']),
        price: this.numberOrNull(listing['price'] ?? item['listingPrice']),
        currencyCode: this.stringOrNull(listing['currencyCode'] ?? item['currencyCode'])
      },
      seller: {
        sellerID: Number(seller['sellerID'] ?? item['sellerID'] ?? 0),
        sellerId: Number(seller['sellerId'] ?? item['sellerId'] ?? 0),
        sellerName: String(seller['sellerName'] ?? item['sellerName'] ?? ''),
        userName: this.stringOrNull(seller['userName'] ?? item['sellerHandle']),
        avatarUrl: this.stringOrNull(seller['avatarUrl'] ?? item['sellerAvatarUrl'])
      },
      ...(live ? {
        live: {
          liveID: Number(live['liveID'] ?? 0),
          channelName: this.stringOrNull(live['channelName']),
          title: this.stringOrNull(live['title']),
          coverImageUrl: this.stringOrNull(live['coverImageUrl']),
          viewerCount: Number(live['viewerCount'] ?? 0),
          startedAt: this.stringOrNull(live['startedAt']),
          status: this.stringOrNull(live['status']),
          isLive: Boolean(live['isLive']),
          endedAt: this.stringOrNull(live['endedAt']),
          replayUrl: this.stringOrNull(live['replayUrl']),
          recordingStatus: this.stringOrNull(live['recordingStatus']),
          recordingDurationSeconds: Number(live['recordingDurationSeconds'] ?? 0)
        }
      } : {})
    };
  }

  private normalizeComment(item: Record<string, unknown>): FeedComment {
    const id = Number(item['commentID'] ?? item['commentId'] ?? item['id'] ?? 0);

    return {
      id,
      commentID: id,
      commentId: id,
      authorName: this.stringOrNull(item['authorName']),
      authorAvatarUrl: this.stringOrNull(item['authorAvatarUrl']),
      comment: String(item['comment'] ?? ''),
      createdAt: this.stringOrNull(item['createdAt'])
    };
  }

  private extractData(response: unknown): Record<string, unknown> {
    const first = Array.isArray(response) ? response[0] : response;
    if (!this.isRecord(first)) {
      return {};
    }
    return this.isRecord(first['data']) ? first['data'] : first;
  }

  private toArray(value: unknown): Record<string, unknown>[] {
    return Array.isArray(value) ? value.filter(this.isRecord) : [];
  }

  private stringOrNull(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  private numberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
